import { sameSelectedOwner, selectedSequence, validSelectedCut } from '../contracts/selectedContinuity.contract.js';
import { SELECTED_CONTINUITY_POLICY } from '../policies/selectedContinuity.policy.js';

/** Owns continuity only. The existing selected recovery/reducer owns projection and prices. */
export function createSelectedContinuity({ getOwner, sequenceRef, recoverCut, applyReplay, recover, clock,
  onPending = value => void value, policy = SELECTED_CONTINUITY_POLICY }) {
  let owner = null, flight = null, epoch = 0, highWater = null, pending = false;
  let burst = 0, windowStart = -Infinity;
  const notify = value => { if (pending !== value) { pending = value; onPending(value); } };
  const cancel = () => {
    epoch += 1;
    if (flight) { flight.cancelled = true; clock.cancel(flight.timer); flight = null; }
    owner = null; highWater = null; burst = 0; windowStart = -Infinity;
    notify(false);
  };
  const sync = () => {
    const next = getOwner();
    if (!sameSelectedOwner(owner, next)) {
      cancel(); owner = next; sequenceRef.current = null;
    }
    return next;
  };
  const retry = () => {
    sync();
    if (!pending || !owner?.supported || flight || !owner?.socket?.connected || !owner.selectionKey) return false;
    const now = clock.now();
    if (now - windowStart >= policy.retryIntervalMs) { burst = 0; windowStart = now; }
    if (burst >= policy.maxBurstAttempts) return false;
    burst += 1;
    const token = { owner, epoch, highWater, since: sequenceRef.current, cancelled: false, timer: null, attempt: burst };
    flight = token;
    const current = () => !token.cancelled && token.owner.socket?.connected && token.epoch === epoch
      && sameSelectedOwner(token.owner, getOwner()) && flight === token;
    const settle = (result) => {
      if (!current()) return;
      clock.cancel(token.timer); flight = null;
      if (result?.applied === true && result?.recovered === true) {
        if (sequenceRef.current >= highWater) {
          notify(false);
        } else retry(); // One bounded follow-up if delivery advanced during the snapshot.
      }
    };
    token.timer = clock.delay(() => {
      if (!current()) return;
      token.cancelled = true; flight = null;
      // Keep last-good and the gap marker. A heartbeat may retry within the same bound.
    }, policy.recoveryTimeoutMs);
    Promise.resolve().then(async () => {
      if (!current()) return null;
      const cut = await recoverCut({ since: token.since, isCancelled: () => !current() });
      if (!current() || !validSelectedCut(cut, token.owner, token.since)) return null;
      for (const envelope of cut.deltas) {
        if (!current()) return null;
        applyReplay(envelope);
        sequenceRef.current = envelope.sequence;
      }
      // REST refresh is an existing overlay, never the continuity authority.
      if (!current()) return null;
      return recover({ isCancelled: () => !current(),
        recoveryOwnerKey: `selected-gap:${token.epoch}:${token.attempt}:${windowStart}` });
    }).then(settle, () => settle(null));
    return true;
  };
  const accept = envelope => {
    const currentOwner = sync();
    if (!currentOwner?.socket?.connected || (currentOwner.supported && !currentOwner.selectionKey)) return 'wrong_scope';
    const room = envelope?.patch?.room ?? envelope?.room;
    if ((currentOwner.supported || room != null) && room !== currentOwner.room) return 'wrong_scope';
    if (!currentOwner.supported) {
      if (Number.isSafeInteger(envelope?.sequence) && Number.isSafeInteger(sequenceRef.current)
          && envelope.sequence <= sequenceRef.current) return 'duplicate';
      sequenceRef.current = envelope?.sequence;
      return 'apply';
    }
    const sequence = selectedSequence(envelope?.sequence);
    if (sequence == null) return 'wrong_scope';
    const previous = selectedSequence(sequenceRef.current);
    if (previous != null && sequence <= previous) return 'duplicate';
    if (currentOwner.supported && (pending || (previous != null && sequence > previous + 1))) {
      highWater = Math.max(highWater ?? previous ?? 0, sequence);
      notify(true); retry(); return 'hold';
    }
    sequenceRef.current = sequence;
    return 'apply';
  };
  return Object.freeze({ accept, retry, sync, cancel,
    state: () => ({ pending, inFlight: flight != null, sequence: sequenceRef.current, highWater, burst }) });
}
